import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  OddsSettingsChunk,
  AppearanceSettingsChunk,
  LanguageSettingsChunk,
  LocalizationSettingsLoaderChunk,
  CountrySettingsChunk,
  BiometricsSettingsChunk,
  NotificationsSettingsChunk
} from "./components/loader.js";
import {
  NativeSelectors,
  SettingsSelectors,
  UISelectors,
  UserContextSelectors
} from "../../redux/selectors/index.js";
import { SettingsDrawerAreaWrapper } from "./SettingsDrawerAreaWrapper.js";
import { DrawerAreaComponentsWrapper } from "../common/Drawer/Area/DrawerAreaWrapper.js";
import { Brand } from "../../configurations/index.js";
import { SettingsActionCreators } from "../../redux/action-creators/index.js";
import { SettingsDrawerBoxWrapper } from "./components/SettingsDrawerBoxWrapper.js";
import { DrawerBoxWrapper } from "../common/Drawer/DrawerBoxWrapper.js";
import { useFeature } from "../../hooks/index.js";
import { AppFeatures } from "../../constants/featureFlags.js";

const useDrawerLoaded = isDrawerActive => {
  const [drawerLoaded, setDrawerLoaded] = useState(false);

  useEffect(() => {
    setDrawerLoaded(true);
  }, [isDrawerActive]);

  return drawerLoaded;
};

const ComBrands = [Brand.Sport_Com, Brand.Sport_MG_MGA];

const SettingsDrawerArea = () => {
  const isDrawerActive = useSelector(SettingsSelectors.general.isDrawerActive);
  const isThemeModeEnabled = useSelector(UISelectors.isThemeModeEnabled);
  const isAuthenticated = useSelector(UserContextSelectors.isAuthenticated);
  const isNative = useSelector(UserContextSelectors.isNative);
  const isBiometricsFlowEnabled = useSelector(
    NativeSelectors.isBiometricsFlowEnabled
  );
  const brandId = useSelector(UserContextSelectors.brandId);
  const isNotificationsEnabled = useFeature(AppFeatures.SETTINGS_NOTIFICATIONS);

  const dispatch = useDispatch();

  const [currentSetting, setCurrentSetting] = useState();
  const isDrawerLoaded = useDrawerLoaded(isDrawerActive);

  const isLocalizationAllowed = useMemo(
    () => ComBrands.some(brand => brand === brandId),
    [brandId]
  );

  const isCountrySettingsAllowed = useMemo(
    () =>
      ComBrands.some(
        brand => brand !== Brand.Sport_MG_MGA && brand === brandId
      ),
    [brandId]
  );

  useEffect(() => {
    if (!isDrawerActive) setCurrentSetting();
  }, [isDrawerActive, setCurrentSetting]);

  useEffect(() => {
    if (isDrawerLoaded)
      dispatch(SettingsActionCreators.general.setDrawerLoaded(true));
  }, [dispatch, isDrawerLoaded]);

  const onSettingClick = useCallback(
    ({ id, expanded }) => {
      setCurrentSetting({ id, expanded });
    },
    [setCurrentSetting]
  );

  const findIfSettingsIsSelected = useCallback(
    settingName =>
      currentSetting?.id === settingName && currentSetting?.expanded === true,
    [currentSetting]
  );

  return (
    <SettingsDrawerAreaWrapper>
      <DrawerAreaComponentsWrapper>
        {isLocalizationAllowed && (
          <>
            <LocalizationSettingsLoaderChunk />
            <SettingsDrawerBoxWrapper>
              <LanguageSettingsChunk
                expanded={findIfSettingsIsSelected("language")}
                onExpand={expanded => {
                  onSettingClick({ id: "language", expanded });
                }}
              />
            </SettingsDrawerBoxWrapper>
            {isCountrySettingsAllowed && (
              <SettingsDrawerBoxWrapper>
                <CountrySettingsChunk
                  expanded={findIfSettingsIsSelected("country")}
                  onExpand={expanded => {
                    onSettingClick({ id: "country", expanded });
                  }}
                />
              </SettingsDrawerBoxWrapper>
            )}
          </>
        )}
        <SettingsDrawerBoxWrapper>
          <OddsSettingsChunk
            expanded={findIfSettingsIsSelected("odds")}
            onExpand={expanded => {
              onSettingClick({ id: "odds", expanded });
            }}
          />
        </SettingsDrawerBoxWrapper>
        {isThemeModeEnabled && (
          <SettingsDrawerBoxWrapper>
            <AppearanceSettingsChunk
              expanded={findIfSettingsIsSelected("appearance")}
              onExpand={expanded => {
                onSettingClick({ id: "appearance", expanded });
              }}
            />
          </SettingsDrawerBoxWrapper>
        )}
        {isNative && isBiometricsFlowEnabled && isAuthenticated && (
          <DrawerBoxWrapper>
            <BiometricsSettingsChunk
              onSelect={() => {
                onSettingClick({ id: "biometrics" });
              }}
            />
          </DrawerBoxWrapper>
        )}
        {isNotificationsEnabled && isAuthenticated && (
          <DrawerBoxWrapper>
            <NotificationsSettingsChunk
              onExpand={() => {
                onSettingClick({ id: "notifications" });
              }}
            />
          </DrawerBoxWrapper>
        )}
      </DrawerAreaComponentsWrapper>
    </SettingsDrawerAreaWrapper>
  );
};

export default React.memo(SettingsDrawerArea);
